/**
 * Overlay host for the Request Correspondence panel (GitLab #1069).
 *
 * CONTROLLED. Open state lives on the screen, because when the panel is docked
 * the screen has to know about it in order to lay out around it — a panel that
 * pushes content cannot own the state deciding whether content is pushed.
 *
 * Since Story 1069-1.2 (C2) this renders ONE thing: the OVERLAY presentation of
 * the panel, for viewports too narrow to dock (every native device and every
 * browser under 1280px). The toggle that used to live here as a floating
 * bottom-right pill is `DiscussionToggle`, a toolbar action the screen renders
 * inside `RequestToolbar.actionTrailing`; the docked presentation is rendered
 * by the screen inside its own layout row. Nothing floats over content.
 *
 * ⚠️ THE OVERLAY PATH NEEDS EVERY PANEL PROP THE DOCKED PATH GETS. `shouldDock`
 * is `isWeb && viewportWidth >= 1280`, so **native never docks** and mounts the
 * panel through HERE. A prop wired into the docked site only is a wide-desktop
 * feature and nothing fails: no type error, no test, no visible defect on the
 * author's screen (that is how read-only-on-closed once shipped for web only;
 * that rule is gone since Story 1.3, the lesson is not).
 * `CorrespondenceLauncher.test.tsx` pins the wiring.
 */
import React from 'react';

import { CorrespondencePanel } from './CorrespondencePanel';

import type {
  SpaceActivity,
  SpaceId,
  SpaceUnread,
} from './correspondence.types';
import type { CorrespondenceSession } from './correspondenceSession';

interface Props {
  open: boolean;
  onToggle: () => void;
  /** True when the screen is rendering the docked panel itself. */
  docked: boolean;
  requestLabel?: string;
  requestId?: string;
  requestorEmail?: string;
  requestorName?: string;
  /**
   * FR54 per space, forwarded to the overlay panel so its space TABS can
   * carry a dot. Without it the toggle's dot leads to a dead end: the panel
   * opens on `internal` while the toggle unions across every space.
   */
  spaceUnread?: SpaceUnread;
  /**
   * The per-space clocks (Story 2.3, D1), forwarded so the overlay panel
   * re-lists the audience it is showing when its clock moves. Native never
   * docks, so a clock wired into the docked mount only would be a
   * wide-desktop feature (the trap in the header).
   */
  spaceActivity?: SpaceActivity;
  /**
   * The summary answered 404 (Story 2.3, D3): the panel stops asking and
   * shows nothing. Forwarded for the same reason as `spaceActivity`.
   */
  accessRevoked?: boolean;
  /**
   * Forwarded to the overlay panel so what it learns reaches the screen, and
   * from there the toggle's own dot (#1069 P3.3). The DOCKED panel is mounted
   * by `RequestDetail` and reports to the same place directly.
   */
  onSpaceUnreadKnown?: (space: SpaceId, hasUnread: boolean) => void;
  /** Forwarded to the overlay panel. See `CorrespondencePanel`. */
  onThreadRead?: () => void;
  /**
   * The screen's session (2026-09-25), the SAME object the docked mount
   * gets, so a dock/overlay swap keeps the user's place. REQUIRED here, where
   * the panel's is optional: this is a production mount, and an overlay
   * that minted its own would compile, render, and silently lose the draft
   * on every resize. See `correspondenceSession.ts`.
   */
  session: CorrespondenceSession;
}

export const CorrespondenceLauncher: React.FC<Props> = ({
  open,
  onToggle,
  docked,
  requestLabel,
  requestId,
  requestorEmail,
  requestorName,
  spaceUnread,
  spaceActivity,
  accessRevoked,
  onSpaceUnreadKnown,
  onThreadRead,
  session,
}) =>
  /* Overlay presentation only. The docked one is mounted by the screen. */
  !docked ? (
    <CorrespondencePanel
      visible={open}
      onClose={onToggle}
      presentation="overlay"
      requestLabel={requestLabel}
      requestId={requestId}
      requestorEmail={requestorEmail}
      requestorName={requestorName}
      spaceUnread={spaceUnread}
      spaceActivity={spaceActivity}
      accessRevoked={accessRevoked}
      onSpaceUnreadKnown={onSpaceUnreadKnown}
      onThreadRead={onThreadRead}
      session={session}
    />
  ) : null;
